/**
 * @file Advertisers management page.
 * @description Provides advertiser listing, stats, create/update modal flow, status toggles, and deletion actions.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Building2, 
  Plus, 
  Edit3, 
  Trash2, 
  Users, 
  TrendingUp, 
  Calendar,
  Mail,
  Phone,
  Globe,
  Play,
  Pause,
  Search,
  X,
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
  AdvertisersService,
  Advertiser,
  AdvertiserFormData,
  AdvertiserStats,
} from '../../services/advertisersService';
import { sanitizePhoneInput, validateMobilePhone } from '../../utils/phoneValidation';
import { apiErrorsToFieldMap, isApiValidationError } from '../../utils/apiValidation';
import { formatCount, parseCount } from '../../utils/formatNumbers';
import { PageHeader, Pagination } from '../../components/ui';
import { cn } from '../../utils/cn';

const MODAL_TRANSITION_MS = 250;
const PER_PAGE = 10;

const Advertisers: React.FC = () => {
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(PER_PAGE);
  const [stats, setStats] = useState<AdvertiserStats>({
    totalAdvertisers: 0,
    totalCampaigns: 0,
    totalLeads: 0,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalAnimatedIn, setModalAnimatedIn] = useState(false);
  const [editingAdvertiser, setEditingAdvertiser] = useState<Advertiser | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof AdvertiserFormData, string>>>({});
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<AdvertiserFormData>({
    name: '',
    email: '',
    phone: '',
    website: '',
  });

  const initialLoadDoneRef = useRef(false);
  const searchDebounceRef = useRef<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchStatsSummary = useCallback(async () => {
    try {
      const { advertisers: all, pagination } = await AdvertisersService.listAdvertisers(1, 500);
      setStats({
        totalAdvertisers: pagination.total,
        totalCampaigns: all.reduce(
          (sum, a) => sum + parseCount(a.total_campaigns),
          0,
        ),
        totalLeads: all.reduce(
          (sum, a) => sum + parseCount(a.total_leads),
          0,
        ),
      });
    } catch {
      // Stats are supplementary; table error state handles main failures.
    }
  }, []);

  const fetchAdvertisers = useCallback(async (page: number = 1) => {
    const isFirstLoad = !initialLoadDoneRef.current;
    setError(null);
    if (isFirstLoad) {
      setIsLoading(true);
    } else {
      setTableRefreshing(true);
    }

    try {
      const { advertisers: rows, pagination } = await AdvertisersService.listAdvertisers(
        page,
        PER_PAGE,
      );
      setAdvertisers(rows);
      setCurrentPage(pagination.page);
      setTotalPages(Math.max(1, pagination.total_page));
      setTotalRecords(pagination.total);
      setPageSize(pagination.per_page || PER_PAGE);
      setStats((prev) => ({
        ...prev,
        totalAdvertisers: pagination.total,
      }));
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred while fetching data.';
      setError(errorMessage);
      setAdvertisers([]);

      if (errorMessage.includes('Session expired')) {
        await Swal.fire({
          title: 'Session Expired',
          text: 'Your session has expired. You will be redirected to login.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } finally {
      setIsLoading(false);
      setTableRefreshing(false);
      initialLoadDoneRef.current = true;
    }
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages || page === currentPage || tableRefreshing) {
        return;
      }
      void fetchAdvertisers(page);
    },
    [fetchAdvertisers, totalPages, currentPage, tableRefreshing],
  );

  const applySearch = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (!q) {
        await fetchAdvertisers(1);
        return;
      }

      setTableRefreshing(true);
      setError(null);
      try {
        const { advertisers: all } = await AdvertisersService.listAdvertisers(1, 500);
        const lower = q.toLowerCase();
        const filtered = all.filter(
          (a) =>
            a.name.toLowerCase().includes(lower) ||
            a.email.toLowerCase().includes(lower) ||
            (a.phone ?? '').toLowerCase().includes(lower) ||
            (a.website ?? '').toLowerCase().includes(lower),
        );
        setAdvertisers(filtered);
        setCurrentPage(1);
        setTotalPages(1);
        setTotalRecords(filtered.length);
        setPageSize(Math.max(filtered.length, 1));
      } catch (e) {
        const errorMessage =
          e instanceof Error ? e.message : 'An unknown error occurred while searching.';
        setError(errorMessage);
        setAdvertisers([]);
      } finally {
        setTableRefreshing(false);
      }
    },
    [fetchAdvertisers],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = window.setTimeout(() => {
        void applySearch(value);
      }, 300);
    },
    [applySearch],
  );

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    if (searchDebounceRef.current !== null) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    void applySearch('');
  }, [applySearch]);

  const refreshAdvertiserTable = useCallback(async () => {
    if (searchQuery.trim()) {
      await applySearch(searchQuery);
    } else {
      await fetchAdvertisers(currentPage);
    }
  }, [searchQuery, applySearch, fetchAdvertisers, currentPage]);

  useEffect(() => {
    void fetchAdvertisers(1);
    void fetchStatsSummary();
  }, [fetchAdvertisers, fetchStatsSummary]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  // Reset form
  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', website: '' });
    setEditingAdvertiser(null);
    setFormErrors([]);
    setFieldErrors({});
    setPhoneTouched(false);
    setModalError(null);
  };

  const validatePhoneField = useCallback((phone: string, markTouched = true) => {
    if (markTouched) setPhoneTouched(true);
    const result = validateMobilePhone(phone);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (result.valid) {
        delete next.phone;
      } else {
        next.phone = result.message ?? 'Please enter a valid phone number';
      }
      return next;
    });
    return result.valid;
  }, []);

  useEffect(() => {
    if (!showCreateModal) {
      setModalAnimatedIn(false);
      return;
    }
    setModalAnimatedIn(false);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setModalAnimatedIn(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [showCreateModal]);

  // Modal ko band karne ka handler
  const handleCloseModal = () => {
    setModalAnimatedIn(false);
    window.setTimeout(() => {
      setShowCreateModal(false);
      resetForm();
    }, MODAL_TRANSITION_MS);
  };

  // Form submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneTouched(true);

    const validation = AdvertisersService.validateAdvertiserForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      setFieldErrors(validation.fieldErrors);
      return;
    }

    setFormErrors([]);
    setFieldErrors({});
    setModalError(null);

    const payload: AdvertiserFormData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: validation.normalizedPhone || formData.phone.trim(),
      website: validation.normalizedWebsite,
    };

    try {
      if (editingAdvertiser) {
        await AdvertisersService.updateAdvertiser(editingAdvertiser.id, payload);
        await Swal.fire({
          title: 'Updated!',
          text: 'Advertiser has been updated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await AdvertisersService.createAdvertiser(payload);
        await Swal.fire({
          title: 'Created!',
          text: 'Advertiser has been created successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      }
      
      await refreshAdvertiserTable();
      await fetchStatsSummary();
      handleCloseModal();
    } catch (error) {
      if (isApiValidationError(error)) {
        const apiFields = apiErrorsToFieldMap(error.errors);
        const nextFieldErrors: Partial<Record<keyof AdvertiserFormData, string>> = {};

        if (apiFields['name']) nextFieldErrors.name = apiFields['name'];
        if (apiFields['email']) nextFieldErrors.email = apiFields['email'];
        if (apiFields['phone']) nextFieldErrors.phone = apiFields['phone'];
        if (apiFields['website']) nextFieldErrors.website = apiFields['website'];

        setFieldErrors(nextFieldErrors);
        setFormErrors(Object.values(apiFields));
        setModalError('Please fix the errors below and try again.');
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      if (errorMessage.includes('Session expired')) {
        await Swal.fire({
          title: 'Session Expired',
          text: 'Your session has expired. You will be redirected to login.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else {
        setModalError(errorMessage);
      }
    }
  };

  // Edit button ka handler
  const handleEdit = (advertiser: Advertiser) => {
    setEditingAdvertiser(advertiser);
    setFormData({
      name: advertiser.name,
      email: advertiser.email,
      phone: advertiser.phone || '',
      website: advertiser.website || '',
    });
    setFormErrors([]);
    setFieldErrors({});
    setPhoneTouched(false);
    setModalError(null);
    setShowCreateModal(true);
  };

  // Delete advertiser handler
  const deleteAdvertiser = async (id: number) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to delete this advertiser. This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await AdvertisersService.deleteAdvertiser(id);
      await Swal.fire({
        title: 'Deleted!',
        text: 'Advertiser has been deleted successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      await refreshAdvertiserTable();
      await fetchStatsSummary();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      if (errorMessage.includes('Session expired')) {
        await Swal.fire({
          title: 'Session Expired',
          text: 'Your session has expired. You will be redirected to login.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else {
        // const errorMessage = errorMessage;
        if (errorMessage.includes('409')) {
          await Swal.fire({
            title: 'Cannot Delete Advertiser',
            text: 'Cannot delete advertiser. This advertiser has active campaigns. Please delete or reassign campaigns first.',
            icon: 'warning',
            confirmButtonText: 'OK',
          });
        } else {
          await Swal.fire({
            title: 'Cannot Delete Advertiser',
            text: errorMessage,
            icon: 'warning',
            confirmButtonText: 'OK',
          });
        }
      }
    }
  };

  // Toggle advertiser status (commented out as not used in UI)
 
  const toggleStatus = async (advertiser: Advertiser) => {
    const newStatus = advertiser.status === '0' ? '1' : '0';
    
    try {
      await AdvertisersService.toggleAdvertiserStatus(advertiser.id, newStatus);
      const Status = newStatus === '0' ? 'Inactive' : 'Active';
      await Swal.fire({
        title: 'Status Updated!',
        text: `Advertiser status has been changed to ${Status}.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      await refreshAdvertiserTable();
      await fetchStatsSummary();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      if (errorMessage.includes('Session expired')) {
        await Swal.fire({
          title: 'Session Expired',
          text: 'Your session has expired. You will be redirected to login.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else {
        await Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  // Format date using service
  const formatDate = AdvertisersService.formatDate;

  const summaryCards = [
    {
      label: 'Total Advertisers',
      value: stats.totalAdvertisers,
      icon: Building2,
      iconWrap: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Total Campaigns',
      value: stats.totalCampaigns,
      icon: TrendingUp,
      iconWrap: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      label: 'Total Leads',
      value: stats.totalLeads,
      icon: Users,
      iconWrap: 'bg-violet-100',
      iconColor: 'text-violet-600',
    },
  ] as const;

  // Loading component
  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Advertisers"
          subtitle="Manage your advertising partners and clients"
          subtitleClassName="text-gray-300 text-sm"
          actions={
            <div className="px-6 py-3 bg-gray-600 rounded-lg animate-pulse">
              <div className="h-5 w-32"></div>
            </div>
          }
        />

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-gray-100 animate-pulse">
                  <div className="h-6 w-6"></div>
                </div>
                <div className="ml-4">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Table Skeleton */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Loading Spinner */}
        <div className="flex justify-center items-center py-8">
          <div className="flex items-center space-x-2 text-gray-600">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-lg font-medium">Loading advertisers...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Advertisers"
          subtitle="Manage your advertising partners and clients"
          subtitleClassName="text-gray-300 text-sm"
        />

        {/* Error Card */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Advertisers</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => void fetchAdvertisers(1)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Advertisers"
        subtitle="Manage your advertising partners and clients"
        subtitleClassName="text-gray-300 text-sm"
        actions={
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="px-6 py-3 bg-white text-gray-800 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center text-sm"
          >
            <Plus size={20} className="mr-2" />
            New Advertiser
          </button>
        }
      />

      {/* Stats Cards */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
        {summaryCards.map(({ label, value, icon: Icon, iconWrap, iconColor }) => (
          <div
            key={label}
            className="rounded-lg p-4 border border-gray-200 bg-white"
          >
            <div className="flex items-center justify-center sm:justify-start flex-col sm:flex-row gap-3">
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${iconWrap}`}
              >
                <Icon size={24} className={iconColor} aria-hidden />
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p
                  className="mt-0.5 text-2xl font-bold tabular-nums tracking-tight text-gray-900 sm:text-3xl"
                  title={formatCount(value)}
                >
                  {formatCount(value)}
                </p>

                <h5 className="text-sm font-medium text-gray-600">{label}</h5>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Advertisers Table */}
      <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <h3 className="shrink-0 text-lg font-semibold text-gray-900">All Advertisers</h3>
          <div className="relative w-full sm:max-w-xs sm:min-w-[16rem]">
            <Search
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search name, email, phone…"
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              aria-label="Search advertisers"
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {tableRefreshing && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]"
            aria-busy="true"
          >
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-14 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Advertiser</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Campaigns</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Total Leads</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {advertisers.map((advertiser, index) => (
                <tr
                  key={advertiser.id}
                  className="transition-colors duration-150 hover:bg-primary-50/40"
                >
                  <td className="px-4 py-4 text-center">
                    <span className="text-sm font-medium tabular-nums text-gray-600">
                      {rangeStart + index}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{advertiser.name}</div>
                      {advertiser.website && (
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <Globe size={14} className="mr-1" />
                          <a 
                            href={advertiser.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:text-blue-600"
                          >
                            {AdvertisersService.formatWebsiteUrl(advertiser.website)}
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail size={14} className="mr-2 text-gray-400" />
                        {advertiser.email}
                      </div>
                      {advertiser.phone && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Phone size={14} className="mr-2 text-gray-400" />
                          {advertiser.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <TrendingUp size={16} className="text-gray-400 mr-1" />
                      {formatCount(parseCount(advertiser.total_campaigns))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Users size={16} className="text-gray-400 mr-1" />
                      {formatCount(parseCount(advertiser.total_leads))}
                    </div>
                  </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleStatus(advertiser)}
                      className={`px-2 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors ${
                        advertiser.status === '1' 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {advertiser.status === '0' ? 'Inactive' : 'Active'}
                    </button>
                  </td> 
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar size={16} className="text-gray-400 mr-1" />
                      {formatDate(advertiser.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleEdit(advertiser)}
                        className="rounded-md p-2 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800"
                        title="Edit Advertiser"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleStatus(advertiser)}
                        className={cn(
                          'rounded-md p-2 transition-colors',
                          advertiser.status === '0'
                            ? 'text-amber-600 hover:bg-amber-50 hover:text-amber-800'
                            : 'text-green-600 hover:bg-green-50 hover:text-green-800',
                        )}
                        title={advertiser.status === '0' ? 'Activate' : 'Deactivate'}
                      >
                        {advertiser.status === '1' ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteAdvertiser(advertiser.id)}
                        className="rounded-md p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-800"
                        title="Delete Advertiser"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {advertisers.length === 0 && !tableRefreshing && (
            <div className="text-center py-12">
              <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                {searchQuery.trim()
                  ? 'No advertisers match your search.'
                  : 'No advertisers found. Create your first advertiser!'}
              </p>
            </div>
          )}
        </div>

        {!searchQuery.trim() && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            itemLabel="advertisers"
            disabled={tableRefreshing}
            ariaLabel="Advertisers pagination"
          />
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center p-4',
            'bg-white/50 backdrop-blur-md transition-opacity duration-250 ease-out',
            modalAnimatedIn ? 'opacity-100' : 'opacity-0',
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="advertiser-modal-title"
        >
          <div
            className={cn(
              'w-full max-w-md rounded-lg bg-white p-6 shadow-2xl transition-all duration-250 ease-out',
              modalAnimatedIn ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-2',
            )}
          >
            <h2 id="advertiser-modal-title" className="text-xl font-bold text-gray-900 mb-4">
              {editingAdvertiser ? 'Edit Advertiser' : 'Create New Advertiser'}
            </h2>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div>
                <label htmlFor="advertiser-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="advertiser-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.name;
                      return next;
                    });
                    setFormErrors((prev) =>
                      prev.filter((error) => !error.toLowerCase().includes('company name')),
                    );
                  }}
                  className={cn(
                    'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    fieldErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-300',
                  )}
                  placeholder="Enter company name"
                  aria-invalid={Boolean(fieldErrors.name)}
                  aria-describedby={fieldErrors.name ? 'advertiser-name-error' : undefined}
                />
                {fieldErrors.name && (
                  <p id="advertiser-name-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.name}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="advertiser-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="advertiser-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.email;
                      return next;
                    });
                    setFormErrors((prev) =>
                      prev.filter((error) => !error.toLowerCase().includes('email')),
                    );
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    fieldErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'advertiser-email-error' : undefined}
                />
                {fieldErrors.email && (
                  <p id="advertiser-email-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="advertiser-phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  id="advertiser-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={16}
                  value={formData.phone}
                  onChange={(e) => {
                    const next = sanitizePhoneInput(e.target.value);
                    setFormData({ ...formData, phone: next });
                    if (phoneTouched) {
                      validatePhoneField(next, false);
                    }
                    setFormErrors((prev) =>
                      prev.filter(
                        (error) =>
                          !error.toLowerCase().includes('phone') &&
                          !error.toLowerCase().includes('mobile'),
                      ),
                    );
                  }}
                  onBlur={(e) => validatePhoneField(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    fieldErrors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g. 9876543210 or +91 9876543210"
                  aria-invalid={Boolean(fieldErrors.phone)}
                  aria-describedby={fieldErrors.phone ? 'advertiser-phone-error' : undefined}
                />
                {fieldErrors.phone && (
                  <p id="advertiser-phone-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="advertiser-website" className="block text-sm font-medium text-gray-700 mb-2">
                  Website <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <input
                  id="advertiser-website"
                  type="text"
                  inputMode="url"
                  value={formData.website}
                  onChange={(e) => {
                    setFormData({ ...formData, website: e.target.value });
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.website;
                      return next;
                    });
                    setFormErrors((prev) =>
                      prev.filter((error) => !error.toLowerCase().includes('website')),
                    );
                    setModalError(null);
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    fieldErrors.website ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g. example.com (saved as https://example.com)"
                />
                {fieldErrors.website && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.website}
                  </p>
                )}
              </div>
              
              {modalError && (
                <div
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                  role="alert"
                >
                  {modalError}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-all"
                >
                  {editingAdvertiser ? 'Update Advertiser' : 'Create Advertiser'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Advertisers;